/**
 * Die Social-Mediathek im Kunden-Admin: hochladen, ansehen, freigeben.
 *
 * Beschrieben wird jedes Medium einmal auf dem eigenen Server der Engine (keine
 * Cloud) — Grundlage fuer Suche und Planung. Verplant wird nur, was der Kunde
 * ausdruecklich zur Verwendung freigibt; bei erkennbaren Personen steht das
 * deutlich am Medium.
 */

import { useRef, useState } from "react";

import type { HubMedium } from "../core/types.js";
import { useMediathek } from "./hooks.js";
import { ERLAUBT } from "./upload.js";

type Filter = "alle" | "frei" | "offen";

const FILTER: { key: Filter; label: string }[] = [
  { key: "alle", label: "Alle" },
  { key: "offen", label: "Ohne Freigabe" },
  { key: "frei", label: "Freigegeben" },
];

export interface MediathekListeProps {
  basePath?: string;
  userName?: string;
}

function groesse(bytes: number | null): string {
  if (!bytes) return "";
  return bytes >= 1_000_000
    ? `${(bytes / 1_000_000).toLocaleString("de-DE", { maximumFractionDigits: 1 })} MB`
    : `${Math.max(1, Math.round(bytes / 1000))} KB`;
}

function dauer(s: number | null): string {
  if (!s) return "";
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.round(s % 60)).padStart(2, "0")}`;
}

export function MediathekListe({ basePath, userName = "" }: MediathekListeProps) {
  const m = useMediathek(basePath);
  const [filter, setFilter] = useState<Filter>("alle");
  const [ziehen, setZiehen] = useState(false);
  const eingabe = useRef<HTMLInputElement>(null);

  const alle = m.data ?? [];
  const liste = alle.filter((x) => (filter === "alle" ? true : filter === "frei" ? x.approved_for_use : !x.approved_for_use));
  const anzahl = (f: Filter) => alle.filter((x) => (f === "alle" ? true : f === "frei" ? x.approved_for_use : !x.approved_for_use)).length;
  const laeuft = m.uploads.some((u) => !u.fertig && !u.fehler);

  function dateienNehmen(dateien: FileList | null) {
    if (dateien && dateien.length) void m.hochladen(Array.from(dateien), userName);
  }

  if (m.loading && !m.data) return <p className="ch-muted">Lädt …</p>;
  if (m.error) return <p className="ch-hinweis">Die Mediathek konnte nicht geladen werden: {m.error}</p>;

  return (
    <div className="ch-mediathek">
      <p className="ch-muted ch-intro">
        Fotos und Videos für Ihre Social-Media-Beiträge. Jedes Medium wird einmal auf unserem eigenen Server
        beschrieben, damit es sich finden und verplanen lässt. Verplant wird nur, was Sie zur Verwendung freigeben.
      </p>

      <div
        className={ziehen ? "ch-upload ch-upload-aktiv" : "ch-upload"}
        onDragOver={(e) => { e.preventDefault(); setZiehen(true); }}
        onDragLeave={() => setZiehen(false)}
        onDrop={(e) => { e.preventDefault(); setZiehen(false); dateienNehmen(e.dataTransfer.files); }}
      >
        <button type="button" className="ch-knopf ch-knopf-primaer" onClick={() => eingabe.current?.click()}>
          Fotos oder Videos hochladen
        </button>
        <span className="ch-muted ch-klein">
          oder hierher ziehen · JPEG, PNG, WebP bis 30 MB · MP4, MOV, WebM bis 1 GB
        </span>
        <input
          ref={eingabe}
          type="file"
          multiple
          accept={ERLAUBT.join(",")}
          hidden
          onChange={(e) => { dateienNehmen(e.target.files); e.target.value = ""; }}
        />
      </div>

      {m.uploads.length > 0 && (
        <ul className="ch-uploads">
          {m.uploads.map((u) => (
            <li key={u.id}>
              <div className="ch-kopf">
                <span className="ch-klein">{u.name}</span>
                <span className={u.fehler ? "ch-klein ch-warn-text" : "ch-klein ch-muted"}>
                  {u.fehler ? u.fehler : u.fertig ? "fertig — wird beschrieben" : `${Math.round(u.anteil * 100)} %`}
                </span>
              </div>
              {!u.fehler && <div className="ch-balken"><span style={{ width: `${Math.round(u.anteil * 100)}%` }} /></div>}
            </li>
          ))}
          {!laeuft && (
            <li>
              <button type="button" className="ch-link ch-klein" onClick={m.erledigteAusblenden}>Liste leeren</button>
            </li>
          )}
        </ul>
      )}

      <div className="ch-filter" role="tablist">
        {FILTER.map((f) => (
          <button
            key={f.key}
            type="button"
            role="tab"
            aria-selected={f.key === filter}
            className={f.key === filter ? "ch-chip ch-chip-aktiv" : "ch-chip"}
            onClick={() => setFilter(f.key)}
          >
            {f.label} <span className="ch-zahl">{anzahl(f.key)}</span>
          </button>
        ))}
      </div>

      {liste.length === 0 ? (
        <p className="ch-muted">
          {alle.length === 0 ? "Noch keine Medien. Laden Sie Fotos oder Videos hoch — gern auch direkt vom Handy." : "Keine Medien in dieser Ansicht."}
        </p>
      ) : (
        <ul className="ch-raster">
          {liste.map((x) => (
            <MediumKachel key={x.id} medium={x} aendern={m.aendern} loeschen={m.loeschen} />
          ))}
        </ul>
      )}
    </div>
  );
}

interface KachelProps {
  medium: HubMedium;
  aendern: (id: number, patch: { approved_for_use?: boolean }) => Promise<void>;
  loeschen: (id: number) => Promise<void>;
}

function MediumKachel({ medium: x, aendern, loeschen }: KachelProps) {
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  async function tun(aktion: () => Promise<void>) {
    setBusy(true);
    setFehler(null);
    try {
      await aktion();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="ch-kachel">
      <div className="ch-medium-bild">
        {x.preview_url || (x.kind === "image" && x.url) ? (
          <img src={x.preview_url || x.url} alt={x.description || x.filename} loading="lazy" />
        ) : (
          <span className="ch-muted ch-klein">ohne Vorschau</span>
        )}
        {x.kind === "video" && <span className="ch-marke">Video {dauer(x.duration_s)}</span>}
      </div>

      <p className="ch-zahlen">
        {x.approved_for_use
          ? <span className="ch-plakette ch-plakette-ok">freigegeben</span>
          : <span className="ch-plakette">ohne Freigabe</span>}
        {x.persons_visible && <span className="ch-plakette ch-plakette-warn">Personen erkennbar</span>}
      </p>

      {x.description ? (
        <p className="ch-text ch-klein">{x.description}</p>
      ) : x.error ? (
        <p className="ch-hinweis ch-klein">{x.error}</p>
      ) : (
        <p className="ch-muted ch-klein">Wird beschrieben …</p>
      )}

      <p className="ch-muted ch-klein">
        {x.filename}
        {x.size_bytes ? ` · ${groesse(x.size_bytes)}` : ""}
        {x.uploaded_by ? ` · ${x.uploaded_by}` : ""}
      </p>

      <div className="ch-aktionen">
        {x.approved_for_use ? (
          <button type="button" className="ch-knopf" disabled={busy}
            onClick={() => void tun(() => aendern(x.id, { approved_for_use: false }))}>Freigabe zurücknehmen</button>
        ) : (
          <button type="button" className="ch-knopf ch-knopf-primaer" disabled={busy}
            onClick={() => {
              if (x.persons_visible && !confirm("Auf diesem Medium sind Personen erkennbar. Liegt deren Einverständnis für Social Media vor?")) return;
              void tun(() => aendern(x.id, { approved_for_use: true }));
            }}>Zur Verwendung freigeben</button>
        )}
        {x.url && <a className="ch-link ch-klein" href={x.url} target="_blank" rel="noopener noreferrer">Original</a>}
        <button type="button" className="ch-link ch-klein" disabled={busy}
          onClick={() => { if (confirm("Dieses Medium endgültig löschen?")) void tun(() => loeschen(x.id)); }}>Löschen</button>
      </div>
      {fehler && <p className="ch-hinweis">{fehler}</p>}
    </li>
  );
}
